import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, query, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import MathGraph from './MathGraph';
import { QuestionText, OptionText, isImageDataOrUrl } from './MathRenderer';
import '../styles/GeneratedQuizScreen.css';
import '../styles/MathGraph.css';

const GeneratedQuizScreen = () => {
    const { quizId } = useParams();
    const navigate = useNavigate();
    const [quizData, setQuizData] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [score, setScore] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [showResult, setShowResult] = useState(false);
    const [timeLeft, setTimeLeft] = useState(null);
    const [timerActive, setTimerActive] = useState(false);
    const [userResponses, setUserResponses] = useState([]);

    // Fisher-Yates shuffle function
    const shuffleArray = (arr) => {
        const shuffled = [...arr];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    };

    // Store shuffled answers stably per question index for this session
    const [shuffledAnswersMap, setShuffledAnswersMap] = useState({});

    useEffect(() => {
        if (questions.length > 0) {
            const map = {};
            questions.forEach((q, idx) => {
                const respuestas = q.respuestas
                    ? (Array.isArray(q.respuestas) ? q.respuestas : Object.values(q.respuestas))
                    : [];
                map[idx] = shuffleArray(respuestas);
            });
            setShuffledAnswersMap(map);
        }
    }, [questions]);

    const touchStartX = useRef(null);

    // Keyboard Shortcuts Navigation & Selection
    useEffect(() => {
        if (loading || !questions.length) return;

        const handleKeyDown = (e) => {
            if (['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) return;

            const currentQ = questions[currentQuestion];
            if (!currentQ) return;

            const answersList = shuffledAnswersMap[currentQuestion] || (
                currentQ.respuestas
                    ? (Array.isArray(currentQ.respuestas) ? currentQ.respuestas : Object.values(currentQ.respuestas))
                    : []
            );

            if (e.key === 'ArrowRight' || e.key.toLowerCase() === 'n') {
                if (currentQuestion < questions.length - 1) {
                    e.preventDefault();
                    setCurrentQuestion(prev => prev + 1);
                    setSelectedAnswer(null);
                }
            } else if (e.key === 'ArrowLeft' || e.key.toLowerCase() === 'p') {
                if (currentQuestion > 0) {
                    e.preventDefault();
                    setCurrentQuestion(prev => prev - 1);
                    setSelectedAnswer(null);
                }
            } else if (selectedAnswer === null) {
                const keyUpper = e.key.toUpperCase();
                const letterIndex = ['A', 'B', 'C', 'D', 'E'].indexOf(keyUpper);
                const numberIndex = ['1', '2', '3', '4', '5'].indexOf(e.key);

                const targetIdx = letterIndex !== -1 ? letterIndex : numberIndex;
                if (targetIdx !== -1 && answersList[targetIdx]) {
                    e.preventDefault();
                    handleAnswer(answersList[targetIdx].id, answersList[targetIdx].correcta);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [loading, questions, currentQuestion, selectedAnswer, shuffledAnswersMap]);

    const handleTouchStart = (e) => {
        if (e.touches && e.touches.length > 0) {
            touchStartX.current = e.touches[0].clientX;
        }
    };

    const handleTouchEnd = (e) => {
        if (touchStartX.current === null || !e.changedTouches || e.changedTouches.length === 0) return;
        const diffX = touchStartX.current - e.changedTouches[0].clientX;
        touchStartX.current = null;

        if (Math.abs(diffX) > 60) {
            if (diffX > 0 && currentQuestion < questions.length - 1) {
                setCurrentQuestion(prev => prev + 1);
                setSelectedAnswer(null);
            } else if (diffX < 0 && currentQuestion > 0) {
                setCurrentQuestion(prev => prev - 1);
                setSelectedAnswer(null);
            }
        }
    };

    useEffect(() => {
        const loadQuiz = async () => {
            try {
                setLoading(true);

                if (!auth.currentUser) throw new Error('Usuario no autenticado');
                const quizDoc = await getDoc(doc(db, 'user_quizzes', auth.currentUser.uid, 'quizzes', quizId));
                if (!quizDoc.exists()) {
                    throw new Error('Cuestionario no encontrado');
                }

                const quizData = quizDoc.data();
                setQuizData(quizData);

                const questionsQuery = query(collection(db, 'user_quizzes', auth.currentUser.uid, 'quizzes', quizId, 'questions'));
                const questionsSnapshot = await getDocs(questionsQuery);
                const questionsData = questionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                const shuffledQuestions = questionsData.sort(() => Math.random() - 0.5);
                setQuestions(shuffledQuestions);

                if (quizData.timeLimit) {
                    setTimeLeft(quizData.timeLimit * 60);
                    setTimerActive(true);
                }
            } catch (error) {
                console.error('Error loading quiz:', error);
                alert('Error al cargar el cuestionario');
                navigate('/dashboard/quiz-generator');
            } finally {
                setLoading(false);
            }
        };

        loadQuiz();
    }, [quizId, navigate]);

    useEffect(() => {
        if (!timerActive || timeLeft === null) return;

        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    setTimerActive(false);
                    endQuiz(score, userResponses);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [timerActive, timeLeft]);

    const endQuiz = (finalScore, finalResponses) => {
        setTimerActive(false);

        if (quizData && auth.currentUser) {
            const completionCount = (quizData.completionCount || 0) + 1;
            const totalScore = (quizData.averageScore || 0) * (quizData.completionCount || 0);
            const newAverage = (totalScore + (finalScore / questions.length * 100)) / completionCount;

            updateDoc(doc(db, 'user_quizzes', auth.currentUser.uid, 'quizzes', quizId), {
                lastUsed: serverTimestamp(),
                completionCount,
                averageScore: newAverage
            });
        }

        navigate('/dashboard/quiz-results', {
            state: {
                score: finalScore,
                totalQuestions: questions.length,
                quizId,
                quizType: quizData?.sourceType === 'document' ? 'VortexDocs' : 'Personalizado',
                responses: finalResponses,
                timeTaken: quizData?.timeLimit ? formatTime((quizData.timeLimit * 60) - timeLeft) : null
            }
        });
    };

    const handleAnswer = (answerId, isCorrect) => {
        setSelectedAnswer(answerId);

        const currentQuestionData = questions[currentQuestion];
        const newResponses = [...userResponses, {
            questionId: currentQuestionData.id,
            questionText: currentQuestionData.pregunta,
            isCorrect,
            correctAnswer: currentQuestionData.respuesta_correcta || 'Desconocida'
        }];
        setUserResponses(newResponses);

        const newScore = isCorrect ? score + 1 : score;
        if (isCorrect) setScore(newScore);

        setTimeout(() => {
            if (currentQuestion + 1 < questions.length) {
                setCurrentQuestion(currentQuestion + 1);
                setSelectedAnswer(null);
            } else {
                endQuiz(newScore, newResponses);
            }
        }, 800);
    };

    const handleRestart = () => {
        setCurrentQuestion(0);
        setScore(0);
        setSelectedAnswer(null);
        setShowResult(false);
        setUserResponses([]);
        setTimeLeft(quizData?.timeLimit ? quizData.timeLimit * 60 : null);
        setTimerActive(!!quizData?.timeLimit);
    };

    const handleBack = () => {
        navigate('/dashboard/quiz-generator');
    };

    const formatTime = (seconds) => {
        if (seconds === null) return '';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    const progressPct = questions.length > 0
        ? Math.round(((currentQuestion + 1) / questions.length) * 100)
        : 0;

    if (loading) {
        return (
            <div className="generated-quiz-container">
                <div className="gqs-loading">
                    <div className="gqs-spinner" />
                    <span>Cargando cuestionario…</span>
                </div>
            </div>
        );
    }

    if (!quizData) {
        return (
            <div className="generated-quiz-container">
                <div className="gqs-state-card">
                    <p className="gqs-error-text">Cuestionario no encontrado</p>
                    <button className="gqs-btn secondary" onClick={handleBack}>← Volver</button>
                </div>
            </div>
        );
    }

    const currentQuestionData = questions[currentQuestion];
    const hasGraph = !!currentQuestionData?.grafica;
    const answersList = shuffledAnswersMap[currentQuestion] || (
        currentQuestionData?.respuestas
            ? (Array.isArray(currentQuestionData.respuestas) ? currentQuestionData.respuestas : Object.values(currentQuestionData.respuestas))
            : []
    );

    return (
        <div className="generated-quiz-container">
            {/* ── Header ── */}
            <div className="quiz-header">
                <div className="gqs-header-top">
                    <button className="gqs-btn-back" onClick={handleBack}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M19 12H5M12 19l-7-7 7-7"/>
                        </svg>
                        Volver
                    </button>
                    <h2>{quizData.name}</h2>
                    <div className="quiz-stats">
                        <span>{currentQuestion + 1} / {questions.length}</span>
                        <span>Puntaje: <strong>{score}</strong></span>
                        {timeLeft !== null && (
                            <span className={`timer ${timeLeft < 60 ? 'danger' : timeLeft < 300 ? 'warning' : ''}`}>
                                ⏱ {formatTime(timeLeft)}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Barra de progreso ── */}
            <div className="progress-bar" role="progressbar" aria-valuenow={progressPct} aria-valuemin={0} aria-valuemax={100}>
                <div className="progress-fill" style={{ width: `${progressPct}%` }} />
            </div>

            {/* ── Contenedor de pregunta ── */}
            <div
                className={`question-container ${hasGraph ? 'has-graph' : ''}`}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
            >
                {/* Badge indicador de gráfica */}
                {hasGraph && (
                    <div className="gqs-graph-badge">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 3v18h18"/><path d="m19 9-5 5-4-4-3 3"/>
                        </svg>
                        Pregunta con gráfica
                    </div>
                )}

                {/* Layout adaptivo: con/sin gráfica */}
                <div className={hasGraph ? 'question-with-graph' : ''}>
                    <div className={hasGraph ? 'question-text-col' : ''}>
                        <div className="question-text">
                            <QuestionText text={currentQuestionData.pregunta} />
                        </div>
                    </div>
                    {hasGraph && (
                        <div className="graph-col">
                            <MathGraph config={currentQuestionData.grafica} />
                        </div>
                    )}
                </div>

                {/* Opciones */}
                <div className="options-grid">
                    {answersList.map((answer, index) => {
                        const letter = String.fromCharCode(65 + index);
                        const isSelected = selectedAnswer === answer.id;
                        const hasImg = isImageDataOrUrl(answer.respuesta);
                        const stateClass = isSelected
                            ? (answer.correcta ? 'correct' : 'incorrect')
                            : '';
                        return (
                            <button
                                key={answer.id || index}
                                className={`answer-button ${stateClass} ${hasImg ? 'has-image' : ''}`}
                                onClick={() => handleAnswer(answer.id, answer.correcta)}
                                disabled={selectedAnswer !== null}
                                aria-label={`Opción ${letter}`}
                                aria-pressed={isSelected}
                            >
                                <span className="option-letter">{letter}</span>
                                <span className="option-text">
                                    <OptionText text={answer.respuesta} />
                                </span>
                                {isSelected && (
                                    <span className={`gqs-answer-icon ${answer.correcta ? 'correct' : 'incorrect'}`}>
                                        {answer.correcta ? '✓' : '✗'}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Hint de teclado (solo desktop) */}
                <div className="gqs-keyboard-hint" aria-hidden="true">
                    Usa <kbd>A</kbd>–<kbd>D</kbd> o <kbd>←</kbd> <kbd>→</kbd> para navegar
                </div>
            </div>

            {/* ── Navegación ── */}
            <div className="quiz-navigation">
                {currentQuestion > 0 && (
                    <button
                        className="nav-btn prev-btn"
                        onClick={() => {
                            setCurrentQuestion(currentQuestion - 1);
                            setSelectedAnswer(null);
                        }}
                    >
                        ← Anterior
                    </button>
                )}
                {currentQuestion < questions.length - 1 && (
                    <button
                        className="nav-btn next-btn"
                        onClick={() => {
                            setCurrentQuestion(currentQuestion + 1);
                            setSelectedAnswer(null);
                        }}
                        disabled={selectedAnswer === null}
                    >
                        Siguiente →
                    </button>
                )}
            </div>
        </div>
    );
};

export default GeneratedQuizScreen;